import { useState } from 'react';
import { ghostButton, Screen, Title } from '../components/ui';
import {
  formatInitial,
  initialValues,
  runTool,
  TOOLS,
  type ToolDef,
} from '../tools/definitions';

export function Tools() {
  const [openId, setOpenId] = useState<string | null>(null);
  const open = TOOLS.find((t) => t.id === openId);

  // One screen, one calculator. No sidebar of other tools to wander into.
  if (open) return <Calculator tool={open} onBack={() => setOpenId(null)} />;

  return (
    <Screen>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Title>Shop tools</Title>
        <div
          style={{ font: '400 13px/1.4 var(--font-body)', color: 'var(--text-4)', textWrap: 'pretty' }}
        >
          Real calculators for real work. Each one shows its working, so using it
          and learning it are the same thing.
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {TOOLS.map((tool) => (
          <button
            key={tool.id}
            type="button"
            onClick={() => setOpenId(tool.id)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              width: '100%',
              minHeight: 64,
              padding: '15px 16px',
              borderRadius: 14,
              background: 'var(--surface)',
              border: '1px solid var(--line)',
              cursor: 'pointer',
              color: 'var(--text)',
              textAlign: 'left',
            }}
          >
            <div style={{ font: '700 16px/1.15 var(--font-display)' }}>{tool.name}</div>
            <div
              style={{
                font: '400 12.5px/1.35 var(--font-body)',
                color: 'var(--text-4)',
                textWrap: 'pretty',
              }}
            >
              {tool.blurb}
            </div>
          </button>
        ))}
      </div>
    </Screen>
  );
}

function Calculator({ tool, onBack }: { tool: ToolDef; onBack: () => void }) {
  // Text is held alongside the numbers so a half-typed "0." survives, exactly
  // as in the drill's numeric input.
  const [text, setText] = useState<Record<string, string>>(() =>
    Object.fromEntries(tool.inputs.map((i) => [i.key, formatInitial(i)])),
  );
  const [values, setValues] = useState<Record<string, number>>(() => initialValues(tool));
  const [showMath, setShowMath] = useState(false);

  const outcome = runTool(tool, values);

  return (
    <Screen>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to the tool list"
          style={{
            width: 44,
            height: 44,
            flex: 'none',
            borderRadius: 10,
            border: '1px solid var(--line-strong)',
            background: 'var(--surface-2)',
            color: 'var(--text-3)',
            font: '600 16px/1 var(--font-display)',
            cursor: 'pointer',
          }}
        >
          ←
        </button>
        <div style={{ font: '700 19px/1.15 var(--font-display)' }}>{tool.name}</div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {tool.inputs.map((input) => (
          <label
            key={input.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '4px 16px',
              borderRadius: 13,
              background: 'var(--surface-2)',
              border: '1px solid var(--line-strong)',
            }}
          >
            <span
              style={{
                flex: 1,
                font: '500 13.5px/1.3 var(--font-body)',
                color: 'var(--text-3)',
              }}
            >
              {input.label}
            </span>
            <input
              inputMode="decimal"
              type="text"
              value={text[input.key] ?? ''}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw !== '' && !/^-?\d*\.?\d*$/.test(raw)) return;
                setText((t) => ({ ...t, [input.key]: raw }));
                const parsed = Number.parseFloat(raw);
                if (Number.isFinite(parsed)) {
                  setValues((v) => ({ ...v, [input.key]: input.integer ? Math.round(parsed) : parsed }));
                }
              }}
              className="num"
              style={{
                width: 120,
                minHeight: 52,
                background: 'transparent',
                border: 'none',
                outline: 'none',
                color: 'var(--text)',
                font: '700 22px/1 var(--font-display)',
                textAlign: 'right',
              }}
            />
            {input.unit && (
              <span
                style={{ font: '600 15px/1 var(--font-display)', color: 'var(--text-4)', width: 34 }}
              >
                {input.unit}
              </span>
            )}
          </label>
        ))}
      </div>

      {'error' in outcome ? (
        <div
          role="status"
          style={{
            padding: '15px 16px',
            borderRadius: 14,
            background: 'var(--red-dim)',
            border: '1px solid var(--red-line)',
            font: '500 14px/1.5 var(--font-body)',
            color: 'var(--text-2)',
            textWrap: 'pretty',
          }}
        >
          {outcome.error}
        </div>
      ) : (
        <>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              padding: 18,
              borderRadius: 'var(--radius-lg)',
              background: 'linear-gradient(155deg,#232b23,#1a1f24 62%)',
              border: '1px solid oklch(0.82 0.17 130 / .22)',
            }}
          >
            {outcome.result.outputs.map((out) => (
              <div
                key={out.label}
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  gap: 12,
                }}
              >
                <div
                  style={{
                    font: '500 13px/1.3 var(--font-body)',
                    color: 'var(--text-4)',
                    textWrap: 'pretty',
                  }}
                >
                  {out.label}
                </div>
                <div
                  className="num"
                  style={{
                    font: out.primary
                      ? '700 26px/1 var(--font-display)'
                      : '600 16px/1 var(--font-display)',
                    letterSpacing: out.primary ? '-.02em' : undefined,
                    color: out.primary ? 'var(--lime-bright)' : 'var(--text-2)',
                    textAlign: 'right',
                  }}
                >
                  {out.value}
                </div>
              </div>
            ))}
          </div>

          {outcome.result.table && (
            <div style={{ overflowX: 'auto' }}>
              <table
                className="num"
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  font: '500 14px/1 var(--font-display)',
                }}
              >
                <thead>
                  <tr>
                    {outcome.result.table.headers.map((h) => (
                      <th
                        key={h}
                        style={{
                          textAlign: 'right',
                          padding: '10px 12px',
                          font: '600 11px/1 var(--font-body)',
                          letterSpacing: '.1em',
                          textTransform: 'uppercase',
                          color: 'var(--text-4)',
                          borderBottom: '1px solid var(--line-strong)',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {outcome.result.table.rows.map((row, i) => (
                    <tr key={i}>
                      {row.map((cell, j) => (
                        <td
                          key={j}
                          style={{
                            textAlign: 'right',
                            padding: '11px 12px',
                            color: j === 0 ? 'var(--text-4)' : 'var(--text)',
                            borderBottom: '1px solid var(--line)',
                          }}
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <button
            type="button"
            onClick={() => setShowMath((s) => !s)}
            style={{ ...ghostButton, width: '100%', flex: 'none' }}
          >
            {showMath ? 'Hide the math' : 'Show the math'}
          </button>

          {showMath && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 11,
                padding: '15px 16px',
                borderRadius: 14,
                background: 'var(--surface)',
                border: '1px solid var(--line-strong)',
              }}
            >
              {outcome.result.steps.map((s, i) => (
                <div key={i} style={{ display: 'flex', gap: 11, alignItems: 'flex-start' }}>
                  <div
                    style={{
                      flex: 'none',
                      width: 20,
                      height: 20,
                      borderRadius: 999,
                      background: 'oklch(0.82 0.17 130 / .16)',
                      color: 'var(--lime-bright)',
                      font: '700 11px/20px var(--font-display)',
                      textAlign: 'center',
                    }}
                  >
                    {i + 1}
                  </div>
                  <div
                    className="num"
                    style={{
                      font: '500 14px/1.45 var(--font-body)',
                      color: 'var(--text-2)',
                      textWrap: 'pretty',
                    }}
                  >
                    {s.text}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Screen>
  );
}
